import { useQuery } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { ListTodo } from 'lucide-react';
import { ACTION_STATUS_LABELS } from '@bp/shared';
import { api, qs } from '@/lib/api';
import type { ActionItem, Store } from '@/lib/types';
import { fmtDate } from '@/lib/utils';
import { Card, Empty, ErrorNote, Loading, PageHeader, Select, Tabs } from '@/components/ui';
import { ActionStatusBadge, PriorityBadge } from '@/components/badges';

type View = 'open' | 'mine' | 'overdue' | 'submitted' | 'closed';

export function TasksPage() {
  const [params, setParams] = useSearchParams();
  const view = (params.get('view') as View) ?? 'open';
  const storeId = params.get('store_id') ?? '';
  const set = (k: string, v: string) => {
    const next = new URLSearchParams(params);
    if (v) next.set(k, v); else next.delete(k);
    setParams(next, { replace: true });
  };
  const query = {
    store_id: storeId,
    status: view === 'closed' ? 'closed' : view === 'submitted' ? 'submitted' : 'open',
    mine: view === 'mine',
    overdue: view === 'overdue',
  };
  const stores = useQuery({ queryKey: ['stores'], queryFn: () => api.get<Store[]>('/api/stores') });
  const q = useQuery({ queryKey: ['tasks', query], queryFn: () => api.get<ActionItem[]>(`/api/action-items${qs(query)}`) });

  return (
    <>
      <PageHeader title="Action items" subtitle="Fixes from failed official audit items. Close each one with photo proof." />
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <Tabs<View>
          value={view}
          onChange={(v) => set('view', v === 'open' ? '' : v)}
          tabs={[
            { value: 'open', label: 'All open' },
            { value: 'mine', label: 'Assigned to me' },
            { value: 'overdue', label: 'Overdue' },
            { value: 'submitted', label: ACTION_STATUS_LABELS.submitted },
            { value: 'closed', label: 'Verified' },
          ]}
        />
        {(stores.data?.length ?? 0) > 1 && (
          <Select className="w-56" value={storeId} onChange={(e) => set('store_id', e.target.value)}>
            <option value="">All stores</option>
            {stores.data!.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </Select>
        )}
      </div>
      {q.isLoading ? <Loading /> : q.error ? <ErrorNote error={q.error} /> : q.data!.length === 0 ? (
        <Card><Empty icon={<ListTodo className="size-8" />} title="Nothing here." /></Card>
      ) : (
        <Card>
          <ul className="divide-y">
            {q.data!.map((t) => (
              <li key={t.id}>
                <Link to={`/tasks/${t.id}`} className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 hover:bg-muted/50">
                  <div className="min-w-0 flex-1">
                    <p className="font-medium">{t.title}</p>
                    <p className="mt-0.5 text-xs text-muted-fg">{t.store_name} · from {fmtDate(t.audit_date ?? null, true)} audit · {t.assignee_name || 'Unassigned'}</p>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <PriorityBadge priority={t.priority} />
                    <ActionStatusBadge status={t.status} overdueDays={t.overdue_days} />
                    <span className="w-24 text-right text-sm text-muted-fg">{t.due_date ? `Due ${fmtDate(t.due_date, true)}` : 'No due date'}</span>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        </Card>
      )}
    </>
  );
}
